import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import {
  PoPageModule,
  PoPageAction,
  PoButtonModule,
  PoFieldModule,
  PoInfoModule,
  PoInfoOrientation,
  PoDividerModule,
  PoWidgetModule,
  PoTagModule,
  PoLoadingModule,
  PoSelectOption,
  PoNotificationService
} from '@po-ui/ng-components';

import { AnexoPayloadItem, AprovarNotaItem, NotasPendentesService } from '../notas-list/notas-pendentes.service';
import { CnaeOption, NotasAdicionarService } from '../notas-adicionar/notas-adicionar.service';
import { AnexosService, AnexoTipo, AnexoParaPreparar, AnexoPreparado, GedFolderCheck, gedIndisponivelMsg } from '../nota-detalhe/anexos.service';
import { toIsoDateLocal } from '../../shared/date-utils';
import { limparNumeroNf } from '../../shared/numero-utils';
import { NotaDetalheService, CteTesOpcao, tesSugeridaAol } from '../nota-detalhe/nota-detalhe.service';
import { PdfParserService } from '../notas-adicionar/pdf-parser/pdf-parser.service';

type AnexoSlot = 'danfe' | 'boleto' | 'romaneio';

// Memo da TES escolhida por estado de geraFinanceiro. Permite restaurar a
// TES original quando o usuario alterna sim/nao/sim sem perder o trabalho.
interface TesMemo {
  sim?: string;
  nao?: string;
}

interface NotaLote {
  filial: string;
  numero: string;
  serie: string;
  fornecedorCod: string;
  fornecedorLoja: string;
  tomadorCnpj: string;
  tomadorNome: string;
  emitenteCnpj: string;
  emitenteNome: string;
  status: string;
  // Tipo do documento (NFe / NFSe / CTe) - vem do backend. Usado para
  // decidir se as TES sugeridas vem da lista NFSe (hardcoded) ou do
  // U_PnfTesCte (planilha REGRAS CTE) atraves do resolverProcesso.
  tipo: string;
  numeroNf: string;
  processo: string;
  geraFinanceiro: string;
  tipoOperacao: string;
  // Opcoes de TES para CTe retornadas por U_PnfTesCte (planilha REGRAS CTE),
  // filtradas por filial + tipoProcesso + Simples Nacional. Populadas pelo
  // resolveTipoProcesso quando nota.tipo === 'CTe'.
  tesCteOpcoes: CteTesOpcao[];
  // Simples Nacional do emitente do CTe (toggle manual na UI). Quando true,
  // resolveTipoProcesso retorna TES de regime SIMPLES (ex.: 410/411); quando
  // false, regime NORMAL. SDS staging nao expoe esse campo de forma
  // confiavel, entao deixamos o usuario decidir.
  simplesNacional: boolean;
  vencimento: string;
  // CNAE do servico (1104.01, 0713.01, etc). Substitui o antigo campo
  // produto (que era display-only). O produto Protheus eh resolvido a
  // partir do CNAE via cnaeList no momento do submit.
  servicoCnae: string;
  // Para CTe: produto frete (D1_COD). 000000000000263 ou 000000000020475.
  // Para NFSe: nao usado (cnaeList resolve produto).
  produto: string;
  // Pedagio do CTe (R$). Pre-preenchido pelo backend a partir do XML CKO,
  // editavel. Backend usa pra gravar F1_VALPEDG e deduzir do D1_TOTAL.
  valorPedagio: number;
  // Para CTe: espelha o flag "Retencao" - true quando a TES tem ICMS (sem
  // "SEM ICMS" na descricao). Usado pelo backend para U_PnfNatFin quando
  // CTe nao tem pedagio (CTe com pedagio cai em natureza fixa 293014).
  retencao: boolean;
  // Tipo de processo resolvido via SW2.W2_ZTPIMPO (encomenda/terceiros/propria).
  // Usado pra sugerir a TES correta. Vazio ate o processo ser validado.
  tipoProcessoResolvido: string;
  // Pasta GED de destino dos anexos. Default = primeira opcao do MV_PNFGFLD.
  gedFolder: string;
  // Anexos tipados (igual notas-adicionar): 1 arquivo por tipo.
  danfeFile: File | null;
  boletoFile: File | null;
  romaneioFile: File | null;
  // Flag de drag por slot (UI feedback).
  danfeDragOver: boolean;
  boletoDragOver: boolean;
  romaneioDragOver: boolean;
  // Status do upload pos-aprovacao (info display por nota).
  uploadStatus: '' | 'enviando' | 'ok' | 'erro';
  uploadMsg: string;
  // Memo da TES escolhida por estado de geraFinanceiro pra restaurar na
  // alternancia sim/nao/sim (vide onGeraFinanceiroChange).
  tesMemo: TesMemo;
  // BU (W2_BU resolvido por SW2 via resolveTipoProcesso). Vai pra D1_ITEMCTA
  // no backend buildItens. Sem isso, o lote nao puxava BU como o individual.
  bu: string;
  // Cache das opcoes do select TES - mantem referencia estavel quando os
  // inputs (geraFinanceiro, tipoProcesso, tesCteOpcoes) nao mudam. Sem
  // isso o po-select perde a selecao ao toggle (recebe array nova em
  // cada CD cycle e o ngOnChanges interno bagunca o display).
  tesOptionsCache: { label: string; value: string }[];
  tesOptionsCacheKey: string;
  // Flag derivada do tomadorCnpj: raiz DAAS (06307786) ou HUBSUL matriz.
  // Habilita o radio "Sem processo". Ver isTomadorDaas().
  tomadorDaas: boolean;
  // Estado do radio "Sem processo". Quando 'sim': processo limpo/disabled
  // e payload envia semProcesso=true. Em CTe de tomador DAAS o backend
  // forca a TES 403; nas demais especies a TES vem do select.
  semProcesso: 'sim' | 'nao';
  // Privilegio SEMPROC vindo da listagem. Habilita o radio em qualquer
  // especie, sem depender do CNPJ do tomador. Chamado 00017826.
  podeSemProcesso: boolean;
}

// Espelha U_PnfDaasTomador no backend. Pra evitar roundtrip extra, calcula
// localmente no listing.
//
// Compara pela RAIZ do CNPJ (revisao 2026-08-12): a lista fechada anterior
// tinha so 06307786000766 e 06307786000251, mas a SM0 de producao tem SETE
// filiais DAAS. Nas cinco de fora o radio "Sem processo" nunca aparecia e o
// operador travava em "Processo obrigatorio para fornecedor nao oculto".
const RAIZ_DAAS = '06307786';
// HUBSUL matriz - mantida da lista original por compatibilidade.
const CNPJ_HUBSUL_MATRIZ = '15235873000150';

function isTomadorDaas(cnpj: unknown): boolean {
  const c = (cnpj ?? '').toString().replace(/\D/g, '');
  return c.startsWith(RAIZ_DAAS) || c === CNPJ_HUBSUL_MATRIZ;
}

@Component({
  selector: 'app-executar-lote',
  imports: [
    CommonModule,
    FormsModule,
    PoPageModule,
    PoButtonModule,
    PoFieldModule,
    PoInfoModule,
    PoDividerModule,
    PoWidgetModule,
    PoTagModule,
    PoLoadingModule
  ],
  templateUrl: './executar-lote.html',
  styleUrls: ['./executar-lote.css'],
})
export class ExecutarLoteComponent implements OnInit {
  notas: NotaLote[] = [];
  saving = false;
  readonly poInfoOrientation = PoInfoOrientation;

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Aprovar NFs', action: () => this.aprovarNFs(), icon: 'an an-check-circle' },
    { label: 'Voltar', action: () => this.router.navigate(['/notas-list']), icon: 'an an-arrow-left' }
  ];

  // CNAEs carregados do backend (U_PnfCnaeList). Cada entrada tem o
  // (cnae, descricao, produto) - usamos o produto pra mandar como
  // D1_COD no backend, e a cnae como display + chave de mapeamento.
  cnaeList: CnaeOption[] = [];
  cnaeOptions: PoSelectOption[] = [];
  carregandoCnaes = false;

  // Pastas GED disponiveis (MV_PNFGFLD). Default = primeira.
  gedFolderOptions: PoSelectOption[] = [];

  // TES quando "Gera Financeiro" = sim (familia "CF" com financeiro).
  // Lista derivada do de-para U_PnfTesNF (depara.tlpp):
  //   Terceiros + fin => issRet ? 256 : 257
  //   Encomenda/propria + fin => issRet ? 264 : 250
  readonly tipoOperacaoOptionsComFin = [
    { label: '(257) CF - IPCOT AQUISICAO DE SERVICO SEM CRED E SEM RET ISS',                     value: '257' },
    { label: '(256) CF - IPCOT AQUISICAO DE SERVICO SEM CRED E COM RET ISS',                     value: '256' },
    { label: '(250) CF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E SEM RET ISS',         value: '250' },
    { label: '(264) CF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E COM RET ISS',         value: '264' },
  ];

  // TES quando "Gera Financeiro" = nao. Apenas TES que NAO geram financeiro
  // (familia "SF" - sem financeiro - do dicionario interno).
  readonly tipoOperacaoOptionsSemFin = [
    { label: '(252) SF - IPCOT AQUISICAO DE SERVICO SEM CRED E SEM ISS COM RETIDOS',             value: '252' },
    { label: '(263) SF - IPCOT AQUISICAO DE SERVICO SEM CRED E COM RET ISS',                     value: '263' },
    { label: '(258) SF - IPCOT AQUISICAO DE SERVICO SEM CRED E SEM RET ISS',                     value: '258' },
    { label: '(261) SF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E SEM ISS COM RETIDOS', value: '261' },
    { label: '(251) SF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E COM RET ISS',         value: '251' },
    { label: '(255) SF - ENCOMENDA/PROPRIA AQUISICAO DE SERVICO COM CRED E SEM RET ISS',         value: '255' },
  ];

  // Opcoes do select TES.
  //   - CTe: APENAS opcoes da planilha REGRAS CTE (tesCteOpcoes), todas (sem
  //     filtrar por dup). Quando vazio (ainda nao resolveu), retorna [] pra
  //     NAO mostrar TES de NFSe erradas. resolveTipoProcesso popula via
  //     U_PnfTesCte. O handler onTipoOperacaoCteChange sincroniza
  //     geraFinanceiro a partir do dup da TES escolhida.
  //   - NFSe: lista hardcoded filtrada por GeraFinanceiro (CF vs SF) E pelo
  //     tipoProcesso resolvido (terceiros => IPCOT; encomenda/propria =>
  //     ENCOMENDA/PROPRIA; sem processo => so' a TES "(sem processo)" 300).
  getTipoOperacaoOptions(nota: NotaLote): { label: string; value: string }[] {
    const tp = (nota.tipoProcessoResolvido || '').toLowerCase().trim();
    // CTe: lista completa das tesCteOpcoes (operador pode editar - regra
    // 2026-05-27). O autoPickTesCte sugere a inicial; key inclui qtd para
    // invalidar quando a lista do backend chega. Quando "Sem processo=Sim"
    // (DAAS), TES eh fixa 403 - key inclui semProcesso pra invalidar.
    const key = nota.tipo === 'CTe'
      ? `cte:${nota.semProcesso}:${nota.tesCteOpcoes.length}`
      : `nfse:${nota.geraFinanceiro}:${tp}`;
    if (key === nota.tesOptionsCacheKey) {
      return nota.tesOptionsCache;
    }
    nota.tesOptionsCacheKey = key;
    if (nota.tipo === 'CTe') {
      // Sem Processo: usa as opcoes da planilha (cProc=C => 407/420 por
      // filial). 403 so' como fallback DAAS quando a planilha nao tem linha.
      if (nota.semProcesso === 'sim' && nota.tesCteOpcoes.length === 0) {
        nota.tesOptionsCache = [{ label: '(403) FRETE - SEM PROCESSO DAAS', value: '403' }];
      } else if (nota.tesCteOpcoes.length > 0) {
        nota.tesOptionsCache = nota.tesCteOpcoes.map(o => ({
          label: `(${o.tes}) ${o.desc}`,
          value: o.tes
        }));
      } else {
        nota.tesOptionsCache = [{ label: 'Preencha o processo para carregar a TES', value: '' }];
      }
    } else {
      const lista = nota.geraFinanceiro === 'sim'
        ? this.tipoOperacaoOptionsComFin
        : this.tipoOperacaoOptionsSemFin;
      nota.tesOptionsCache = this.filtrarTesPorTipoProcesso(lista, tp);
    }
    return nota.tesOptionsCache;
  }

  // Filtra a lista hardcoded de TES NFSe pelo tipo de processo (SW2.W2_ZTPIMPO).
  //   "(sem processo)" -> TES generica (300) - APENAS quando processo vazio
  //   "IPCOT"           -> familia terceiros (conta e ordem)
  //   "ENCOMENDA/PROPRIA" -> familia encomenda/propria
  // Com processo preenchido, a TES (sem processo) eh excluida pra alinhar
  // com a logica do PHP NfseOperation::getTes (sem fallback generico).
  private filtrarTesPorTipoProcesso(
    lista: { label: string; value: string }[],
    tipoProcesso: string
  ): { label: string; value: string }[] {
    if (!tipoProcesso) {
      const semProc = lista.filter(o => /\(sem processo\)/i.test(o.label));
      return semProc.length > 0 ? semProc : lista;
    }
    const semGenerica = lista.filter(o => !/\(sem processo\)/i.test(o.label));
    if (tipoProcesso === 'terceiros') {
      return semGenerica.filter(o => /IPCOT/i.test(o.label));
    }
    if (tipoProcesso === 'encomenda' || tipoProcesso === 'propria') {
      return semGenerica.filter(o => /ENCOMENDA\/PROPRIA/i.test(o.label));
    }
    return semGenerica;
  }

  // Lista de produtos de frete pra CTe (D1_COD). Mesma logica do
  // notas-adicionar e do U_PnfPrdCte no backend.
  readonly produtosCteOptions: { label: string; value: string }[] = [
    { label: '000000000000263 - FRETE S/ VENDA',                   value: '000000000000263' },
    { label: '000000000020475 - FRETE S/ VENDA SIMPLES NACIONAL',  value: '000000000020475' }
  ];

  // Mapeia o regime do emitente para o D1_COD. Espelha U_PnfPrdCte.
  produtoCtePorSn(sn: boolean): string {
    return sn ? '000000000020475' : '000000000000263';
  }

  // Handler do select TES (CTe). Sincroniza Retencao (= NAO se descricao
  // contem "SEM ICMS"/"S/ICMS") e memoriza a TES por estado de geraFinanceiro.
  //
  // NAO sobrescreve geraFinanceiro - regra atualizada 2026-05-29. Antes
  // virava o radio do user pra TES.dup, mas isso disparava validacao de
  // boleto/vencimento errada quando o user explicitamente marcou "Nao".
  // Inconsistencia TES vs radio fica visivel; backend valida.
  onTipoOperacaoCteChange(nota: NotaLote, v: string): void {
    nota.tipoOperacao = v;
    if (nota.tipo === 'CTe') {
      const opcao = nota.tesCteOpcoes.find(o => o.tes === v);
      if (opcao) {
        nota.retencao = !/sem\s+icms|s\/\s*icms/i.test(opcao.desc);
      }
    }
    if (v) {
      nota.tesMemo[nota.geraFinanceiro as 'sim'|'nao'] = v;
    }
  }

  // Handler do select TES (NFSe). Memoriza a TES escolhida por estado de
  // geraFinanceiro. Para CTe usar onTipoOperacaoCteChange.
  onTipoOperacaoNfseChange(nota: NotaLote, v: string): void {
    nota.tipoOperacao = v;
    if (v) {
      nota.tesMemo[nota.geraFinanceiro as 'sim'|'nao'] = v;
    }
  }

  // Auto-escolhe a TES de CTe definitiva, baseado no contexto da nota
  // (geraFinanceiro + valorPedagio + descricao). O cliente decidiu que o
  // operador NAO escolhe TES de CTe - o template trava o select e mostra
  // somente a opcao calculada aqui. Sempre roda (force=true) para
  // recalcular ao mudar geraFinanceiro/pedagio/processo.
  //
  // Heuristica (em ordem):
  //   1) Pedagio: valorPedagio>0 -> so TES com "PEDAGIO" no desc; senao,
  //      so TES sem "PEDAGIO" (evita pegar TES de pedagio sem pedagio real)
  //   2) Gera Financeiro: filtra por dup === (geraFinanceiro==='sim')
  //   3) Prefere TES com marcador C/FINANCEIRO ou S/FINANCEIRO no desc
  //      consistente com o ramo dup (passo 2 ja eliminou inconsistencias,
  //      mas esse refino afasta TES genericas se houver explicit)
  //   4) Exclui TES com "SEM ICMS"/"S/ICMS" (excecoes) se houver outras
  //   5) Pega a primeira da lista filtrada
  autoPickTesCte(nota: NotaLote): void {
    if (nota.tipo !== 'CTe' || !nota.tesCteOpcoes.length) { return; }
    let candidatos = nota.tesCteOpcoes.slice();

    const temPedagio = (nota.valorPedagio || 0) > 0;
    const comPedagio = candidatos.filter(o => /pedagio/i.test(o.desc));
    const semPedagio = candidatos.filter(o => !/pedagio/i.test(o.desc));
    if (temPedagio && comPedagio.length > 0) { candidatos = comPedagio; }
    else if (!temPedagio && semPedagio.length > 0) { candidatos = semPedagio; }

    const wantDup = nota.geraFinanceiro === 'sim';
    const dupMatch = candidatos.filter(o => o.dup === wantDup);
    if (dupMatch.length === 0) {
      // Nenhuma TES com dup compativel: NAO sobrescreve geraFinanceiro
      // (evita inconsistencia UI vs estado) e PRESERVA a TES atual
      // (regra cliente 2026-05-28). Antes zerava - operador reclamou.
      this.poNotification.warning(
        `Sem TES de CTe disponivel para ${wantDup ? '"com financeiro"' : '"sem financeiro"'} ` +
        `nesse processo - mantida a TES atual.`
      );
      return;
    }
    candidatos = dupMatch;

    const explicitFin = candidatos.filter(o =>
      wantDup ? /c\/\s*financeiro/i.test(o.desc) : /s\/\s*financeiro/i.test(o.desc)
    );
    if (explicitFin.length > 0) { candidatos = explicitFin; }

    const semIcmsExc = candidatos.filter(o => !/sem\s*icms|s\/\s*icms/i.test(o.desc));
    if (semIcmsExc.length > 0) { candidatos = semIcmsExc; }

    // Operacao AOL da Logistica Matriz: a 424 vence a heuristica acima quando
    // o CTe e de um dos fornecedores da lista. Sugestao apenas - o operador
    // troca no select conforme a finalidade do frete (chamado 00018371).
    const tesAol = tesSugeridaAol(nota.filial, nota.fornecedorCod, nota.tesCteOpcoes);
    const escolhida = tesAol
      ? (candidatos.find(o => o.tes === tesAol)
         ?? nota.tesCteOpcoes.find(o => o.tes === tesAol)!)
      : candidatos[0];
    nota.tipoOperacao = escolhida.tes;
    // geraFinanceiro ja eh consistente (dup === wantDup)
    nota.retencao = !/sem\s+icms|s\/\s*icms/i.test(escolhida.desc);
    nota.tesOptionsCacheKey = '';
  }

  // Handler do select Produto Frete (CTe). Atualiza nota.produto.
  onProdutoCteChange(nota: NotaLote, v: string): void {
    nota.produto = v;
  }

  // Handler do radio "Sem processo" (CTe de tomador DAAS ou privilegio
  // SEMPROC em qualquer especie).
  //   Sim -> limpa processo; em CTe recarrega a TES da planilha REGRAS CTE,
  //          nas demais especies a lista passa a oferecer a TES generica
  //          "(sem processo)" porque o tipoProcesso ficou vazio
  //   Nao -> volta TES auto-pickada, libera processo
  onSemProcessoChange(nota: NotaLote, v: string): void {
    nota.semProcesso = v as 'sim' | 'nao';
    if (v === 'sim') {
      nota.processo = '';
      nota.tipoProcessoResolvido = '';
      nota.tipoOperacao = '';
      nota.tesOptionsCacheKey = '';
      if (nota.tipo === 'CTe') {
        // CTe: carrega as TES de Sem Processo da planilha (cProc=S; a filial
        // sem linha "S" cai em CONTA E ORDEM => 407/420). A 403 deixou de ser
        // fixa - vira fallback DAAS no getter quando a planilha nao tem linha
        // para a filial.
        this.resolveTipoProcesso(nota);
      }
      // NFS-e: nada a carregar. Com tipoProcessoResolvido vazio o
      // getTipoOperacaoOptions ja devolve a TES generica "(sem processo)",
      // que o operador confirma no select - o backend RECUSA a entrega sem
      // TES fora do CTe. Chamado 00017826.
    } else {
      nota.tipoOperacao = '';
      nota.tesOptionsCacheKey = '';
      this.autoPickTesCte(nota);
    }
  }

  // Handler do radio "Gera Financeiro". Antes de trocar, memoriza a TES atual
  // pro estado anterior. Apos trocar, tenta restaurar a TES memorizada do
  // novo estado (se ainda valida); caso contrario, cai no autoPick (CTe) ou
  // defaultTesParaFin (NFSe). Isso preserva a escolha do usuario quando ele
  // alterna sim/nao/sim.
  onGeraFinanceiroChange(nota: NotaLote, v: string): void {
    if (nota.tipoOperacao) {
      nota.tesMemo[nota.geraFinanceiro as 'sim'|'nao'] = nota.tipoOperacao;
    }
    nota.geraFinanceiro = v;
    // Sem financeiro: limpa vencimento + boleto anexado (regra 2026-05-29).
    // Boleto so faz sentido com financeiro; vencimento idem.
    if (v !== 'sim') {
      nota.vencimento = '';
      nota.boletoFile = null;
    }
    // CTe: re-auto-pick baseado no contexto. Excecao: "Sem Processo DAAS"
    // (semProcesso=sim) mantem TES fixa em 403 indiferente do geraFinanceiro.
    // Demais cenarios: autoPickTesCte ja preserva a TES atual quando nao
    // ha match de dup (regra 2026-05-28).
    if (nota.tipo === 'CTe') {
      // Sem Processo: a planilha traz 405 (s/fin) e 407 (c/fin) na mesma
      // chamada - autoPick re-seleciona por geraFinanceiro (dup). 403 so'
      // como fallback quando a planilha nao tem linha (filial DAAS). NUNCA
      // forcar 403 com a lista preenchida (sairia fora da lista = vazio).
      if (nota.semProcesso === 'sim' && nota.tesCteOpcoes.length === 0) {
        nota.tipoOperacao = '403';
        nota.tesOptionsCacheKey = '';
        return;
      }
      this.autoPickTesCte(nota);
      return;
    }
    // NFSe: usa memo se valido, senao default coerente
    const memo = nota.tesMemo[v as 'sim'|'nao'];
    const opts = this.getTipoOperacaoOptions(nota);
    if (memo && opts.some(o => o.value === memo)) {
      nota.tipoOperacao = memo;
      return;
    }
    const aindaValida = opts.some(o => o.value === nota.tipoOperacao);
    if (!aindaValida) {
      nota.tipoOperacao = this.defaultTesParaFin(v, opts, nota.tipoProcessoResolvido);
    }
  }

  // Mapeamento de default herdado de depara.tlpp (U_PnfTesNF):
  //   fin=sim + terceiros           -> 257
  //   fin=sim + encomenda/propria   -> 250
  //   fin=nao + terceiros           -> 252 (regra 2026-05-27: com retidos)
  //   fin=nao + encomenda/propria   -> 261 (regra 2026-05-27: com retidos)
  // Quando o tipoProcesso nao foi resolvido ainda, usa fallback razoavel
  // (com retidos federais, sem ISS) para evitar campo vazio.
  private defaultTesParaFin(
    v: string,
    opts: { label: string; value: string }[],
    tipoProcesso = ''
  ): string {
    const isEncProp = tipoProcesso === 'encomenda' || tipoProcesso === 'propria';
    const isTerc    = tipoProcesso === 'terceiros';
    if (v === 'sim') {
      if (isEncProp && opts.some(o => o.value === '250')) { return '250'; }
      if (isTerc    && opts.some(o => o.value === '257')) { return '257'; }
      // Sem tipoProcesso: cai no terceiros (TES mais comum no portal).
      if (opts.some(o => o.value === '257')) { return '257'; }
      return opts.length > 0 ? opts[0].value : '';
    }
    // fin = nao
    if (isEncProp && opts.some(o => o.value === '261')) { return '261'; }
    // IPCOT s/financeiro: default 252 (sem ISS com retidos federais), nao 258.
    // Motivo: cenario comum tem retidos federais (PIS/COFINS/IR/CSLL) mesmo
    // sem ISS retido - regra 2026-05-27.
    if (isTerc    && opts.some(o => o.value === '252')) { return '252'; }
    // Sem tipoProcesso: fallback terceiros 252.
    if (opts.some(o => o.value === '252')) { return '252'; }
    return opts.length > 0 ? opts[0].value : '';
  }

  // Handler do toggle "Simples Nacional" da linha. Quando o usuario muda,
  // refaz o resolveTipoProcesso pra trazer as TES do regime correto
  // (NORMAL vs SIMPLES). So faz sentido pra CTe; pra NFSe ignora.
  onSimplesNacionalChange(nota: NotaLote, v: boolean): void {
    nota.simplesNacional = v;
    if (nota.tipo === 'CTe') {
      // Atualiza produto frete pelo regime e re-resolve TES (lista muda)
      nota.produto = this.produtoCtePorSn(v);
      nota.tipoOperacao = '';
      if (nota.processo) {
        this.resolveTipoProcesso(nota);
      }
    }
  }

  // Resolve o processo via SW2 (W2_ZTPIMPO) pra capturar o tipo (encomenda/
  // terceiros/propria). Apos resolver, re-sugere a TES default.
  // Chamado no blur do campo Processo e tambem no ngOnInit para notas que
  // ja vem com processo pre-preenchido.
  resolveTipoProcesso(nota: NotaLote): void {
    let proc = (nota.processo || '').trim();
    nota.tipoProcessoResolvido = '';
    if (!proc) {
      // NFSe sem processo: nao tem o que resolver. CTe sem processo:
      // dispara mesmo assim com placeholder pra carregar tesCteOpcoes
      // via override de tipoProcesso ('encomenda' default).
      if (nota.tipo !== 'CTe') { return; }
      proc = '_placeholder_';
    }
    // filial + sn sao necessarios para U_PnfTesCte filtrar TES da planilha
    // REGRAS CTE (filtragem por filial Ascensus e regime do emitente).
    // sn vem do toggle "Simples Nacional" do usuario na linha da nota -
    // SDS staging nao expoe esse campo de forma confiavel pra detectar
    // automaticamente.
    this.notaDetalheService.resolverProcesso({
      processo:     proc,
      filial:       nota.filial,
      sn:           nota.simplesNacional,
      // Pra CTes em teste (processo nao cadastrado em SW2), manda override
      // baseado no que ja temos resolvido (ou 'encomenda' default) pro backend
      // popular tesCteOpcoes mesmo sem SW2 encontrar o processo.
      // Sem Processo: manda 'semprocesso' => backend cProc=S => carrega a
      // linha "S" propria da filial (ex.: 403 na 006001007, 498 na 010010001)
      // e, quando a filial nao tem linha "S", o U_PnfTesCte ja faz fallback
      // pra "C" (407/420) sozinho. Antes mandava 'terceiros' (cProc=C fixo),
      // o que ignorava as regras de Sem Processo e divergia do nota-detalhe.
      // Demais casos usam o tipo resolvido (ou 'encomenda' default p/ CTe em teste).
      tipoProcesso: nota.tipo === 'CTe'
        ? (nota.semProcesso === 'sim' ? 'semprocesso' : (nota.tipoProcessoResolvido || 'encomenda'))
        : undefined
    }).subscribe({
      next: r => {
        // tesCteOpcoes sempre populado, mesmo quando processo nao existe
        // em SW2 - permite o select CTe abrir com opcoes do fallback.
        nota.tesCteOpcoes = r.tesCteOpcoes ?? [];
        if (r.processoExiste === false) {
          if (nota.tipo !== 'CTe') {
            this.poNotification.warning(`Processo ${proc} nao localizado no Protheus.`);
            return;
          }
          // CTe com processo nao cadastrado: ja temos tesCteOpcoes do fallback,
          // segue pra auto-pick.
        }
        nota.tipoProcessoResolvido = r.tipoProcesso ?? '';
        // BU do processo (SW2.W2_BU) - sem isso o backend nao consegue setar
        // D1_ITEMCTA no item. Lote estava perdendo isso porque nao guardava
        // nem enviava no payload (vs individual que ja propagava).
        nota.bu = r.bu ?? '';
        if (nota.tipo === 'CTe') {
          this.autoPickTesCte(nota);
        } else {
          const opts = this.getTipoOperacaoOptions(nota);
          nota.tipoOperacao = this.defaultTesParaFin(nota.geraFinanceiro, opts, nota.tipoProcessoResolvido);
        }
        if (r.processoEncerrado) {
          this.poNotification.error(`Processo ${proc} encerrado. Nao sera possivel incluir a nota.`);
        }
      },
      error: () => this.poNotification.warning(`Nao foi possivel validar o processo ${proc} agora.`)
    });
  }

  readonly geraFinanceiroOptions = [
    { label: 'Sim', value: 'sim' },
    { label: 'Não', value: 'nao' },
  ];

  readonly semProcessoOptions = [
    { label: 'Sim', value: 'sim' },
    { label: 'Não', value: 'nao' },
  ];

  constructor(
    private router: Router,
    private poNotification: PoNotificationService,
    private service: NotasPendentesService,
    private notasAdicionarService: NotasAdicionarService,
    private anexosService: AnexosService,
    private notaDetalheService: NotaDetalheService,
    private pdfParser: PdfParserService
  ) {}

  ngOnInit(): void {
    this.carregarCnaes();
    this.carregarGedFolders();

    const state = history.state as { notas?: any[] };
    if (!state?.notas?.length) {
      this.router.navigate(['/notas-list']);
      return;
    }

    this.notas = state.notas.map((n: any): NotaLote => ({
      filial:           n.filial         || '',
      numero:           String(n.numero || n.numeroNf || ''),
      serie:            n.serie          || '',
      fornecedorCod:    n.fornecedorCod  || '',
      fornecedorLoja:   n.fornecedorLoja || '',
      tomadorCnpj:      n.tomadorCnpj    || '',
      tomadorNome:      n.tomadorNome    || '',
      emitenteCnpj:     n.emitenteCnpj   || n.fornecedorCnpj || '',
      emitenteNome:     n.fornecedor     || n.emitenteNome   || '',
      status:           n.status         || 'Pendente',
      tipo:             String(n.tipo    || 'NFSe'),
      numeroNf:         limparNumeroNf(String(n.numeroExibe || n.numero || n.numeroNf || '')),
      processo:         n.processo       || '',
      geraFinanceiro:   'nao',
      tipoOperacao:     '',
      tesCteOpcoes:     [],
      // simplesNacional vem do backend (XML <indSN> via CKO). Usuario
      // pode sobrescrever via toggle na UI caso o auto-detect falhe.
      simplesNacional:  !!n.simplesNacional,
      vencimento:       '',
      // CNAE vem da nota (DT_PRODFOR/cnaeNorm) - pre-seleciona quando
      // possivel; usuario pode trocar antes de aprovar.
      // IMPORTANTE: prioriza cnaeNorm (formato "1104.01") sobre cnae cru
      // (formato "110401") porque cnaeOptions usa o formato normalizado -
      // sem isso o po-select nao acha match e o campo fica vazio.
      servicoCnae:      (n.cnaeNorm || n.cnae || '').toString().trim(),
      // CTe: produto frete derivado do regime SN; NFSe: vazio (cnae resolve)
      produto:          String(n.tipo) === 'CTe' ? this.produtoCtePorSn(!!n.simplesNacional) : '',
      // Pedagio pre-preenchido pelo backend (XML CKO); usuario edita se quiser
      valorPedagio:     Number(n.valorPedagio || 0),
      // Retencao default true pra CTe (refinado depois pela TES escolhida)
      retencao:         String(n.tipo) === 'CTe',
      tipoProcessoResolvido: '',
      gedFolder:        '',
      danfeFile:        null,
      boletoFile:       null,
      romaneioFile:     null,
      danfeDragOver:    false,
      boletoDragOver:   false,
      romaneioDragOver: false,
      uploadStatus:     '',
      uploadMsg:        '',
      tesMemo:          {},
      tesOptionsCache:    [],
      tesOptionsCacheKey: '',
      bu:               '',
      // CTe Sem Processo DAAS: derivado do tomadorCnpj (SM0 da filial).
      // So aparece pra CTe.
      tomadorDaas: String(n.tipo) === 'CTe' && isTomadorDaas(n.tomadorCnpj),
      // Privilegio SEMPROC resolvido pelo backend na propria listagem.
      // Somado ao tomadorDaas acima, define se o radio aparece.
      podeSemProcesso: !!n.podeSemProcesso,
      semProcesso: 'nao',
    }));

    // Pre-fill da TES. Para NFSe usa o fallback hardcoded; pra CTe dispara
    // resolveTipoProcesso (sempre, com ou sem processo) pra carregar a
    // lista da planilha REGRAS CTE - o select fica vazio ate isso voltar.
    for (const n of this.notas) {
      if (n.tipo === 'CTe') {
        if (!n.tipoProcessoResolvido) { n.tipoProcessoResolvido = 'encomenda'; }
        this.resolveTipoProcesso(n);
        continue;
      }
      // NFSe
      if (!n.tipoOperacao) {
        const opts = this.getTipoOperacaoOptions(n);
        n.tipoOperacao = this.defaultTesParaFin(n.geraFinanceiro, opts, n.tipoProcessoResolvido);
      }
      if (n.processo) { this.resolveTipoProcesso(n); }
    }
  }

  // ---------------------------------------------------------------------
  // Setup
  // ---------------------------------------------------------------------

  private carregarCnaes(): void {
    this.carregandoCnaes = true;
    this.notasAdicionarService.cnaes().subscribe({
      next: list => {
        this.cnaeList = list;
        this.cnaeOptions = list.map((c: CnaeOption) => ({
          label: `${c.cnae} - ${c.descricao}`,
          value: c.cnae
        }));
        this.carregandoCnaes = false;
      },
      error: () => {
        this.poNotification.warning('Nao foi possivel carregar a lista de CNAEs.');
        this.carregandoCnaes = false;
      }
    });
  }

  private carregarGedFolders(): void {
    this.anexosService.gedFolders().subscribe({
      next: folders => {
        this.gedFolderOptions = (folders ?? []).map(f => ({ label: f, value: f }));
        const def = this.gedFolderOptions[0]?.value as string | undefined;
        if (def) {
          for (const n of this.notas) {
            if (!n.gedFolder) { n.gedFolder = def; }
          }
        }
      },
      error: () => {
        this.poNotification.warning('Nao foi possivel carregar as pastas GED.');
      }
    });
  }

  // ---------------------------------------------------------------------
  // Anexos: 3 slots tipados (DAN/BOL/ROM) com drag-and-drop, igual
  // notas-adicionar. Limite 10 MB por arquivo (AnexosService.MAX_BYTES).
  // ---------------------------------------------------------------------

  onFileSelected(event: Event, nota: NotaLote, slot: AnexoSlot): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) { return; }
    this.assignFile(nota, slot, input.files[0]);
    input.value = '';
  }

  onSlotDragOver(event: DragEvent, nota: NotaLote, slot: AnexoSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragFlag(nota, slot, true);
  }

  onSlotDragLeave(event: DragEvent, nota: NotaLote, slot: AnexoSlot): void {
    event.preventDefault();
    this.setDragFlag(nota, slot, false);
  }

  onSlotDrop(event: DragEvent, nota: NotaLote, slot: AnexoSlot): void {
    event.preventDefault();
    event.stopPropagation();
    this.setDragFlag(nota, slot, false);
    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) { return; }
    this.assignFile(nota, slot, files[0]);
  }

  clearFile(nota: NotaLote, slot: AnexoSlot): void {
    if (slot === 'danfe')    nota.danfeFile = null;
    if (slot === 'boleto')   nota.boletoFile = null;
    if (slot === 'romaneio') nota.romaneioFile = null;
  }

  getFile(nota: NotaLote, slot: AnexoSlot): File | null {
    if (slot === 'danfe')    return nota.danfeFile;
    if (slot === 'boleto')   return nota.boletoFile;
    if (slot === 'romaneio') return nota.romaneioFile;
    return null;
  }

  private assignFile(nota: NotaLote, slot: AnexoSlot, file: File): void {
    if (file.size > AnexosService.MAX_BYTES) {
      this.poNotification.warning(`${file.name}: excede 10 MB.`);
      return;
    }
    if (slot === 'danfe')    nota.danfeFile = file;
    if (slot === 'boleto')   nota.boletoFile = file;
    if (slot === 'romaneio') nota.romaneioFile = file;
    // Anexo DANFE: parseia o PDF e atualiza campos da nota (pedagio, SN
    // pra CTe; processo/retencoes pra NFSe). Best-effort - falha silenciosa.
    if (slot === 'danfe') {
      void this.parseAndApplyDanfe(nota, file);
    }
  }

  // Le o PDF do DANFE/DACTE e aplica campos relevantes na nota. Pra CTe,
  // atualiza valorPedagio + simplesNacional + produto (e dispara
  // resolveTipoProcesso pra atualizar TES). Pra NFSe, sobrescreve processo
  // se vazio. Erros viram warning sem interromper o fluxo.
  private async parseAndApplyDanfe(nota: NotaLote, file: File): Promise<void> {
    try {
      const data = await this.pdfParser.parseDanfe(file);
      if (data.source === 'cte' && nota.tipo === 'CTe') {
        if (data.valorPedagio && data.valorPedagio > 0) {
          nota.valorPedagio = data.valorPedagio;
        }
        if (data.simplesNacional !== undefined) {
          nota.simplesNacional = data.simplesNacional;
          nota.produto = this.produtoCtePorSn(nota.simplesNacional);
        }
        if (data.processo && !nota.processo) {
          nota.processo = data.processo;
        }
        // Re-resolve com novos sn/pedagio (lista de TES muda + auto-pick)
        nota.tipoOperacao = '';
        this.resolveTipoProcesso(nota);
        this.poNotification.success(`${file.name}: pedagio/SN extraidos do DACTE.`);
      } else if (data.source !== 'cte') {
        // Qualquer DANFE NFSe (curitiba/navegantes/itapoa/joinville/generic):
        // aproveita pra preencher processo se a nota ainda nao tem.
        if (data.processo && !nota.processo) {
          nota.processo = data.processo;
          this.resolveTipoProcesso(nota);
        }
      }
    } catch {
      this.poNotification.warning(`Nao foi possivel ler o PDF: ${file.name}`);
    }
  }

  private setDragFlag(nota: NotaLote, slot: AnexoSlot, value: boolean): void {
    if (slot === 'danfe')    nota.danfeDragOver = value;
    if (slot === 'boleto')   nota.boletoDragOver = value;
    if (slot === 'romaneio') nota.romaneioDragOver = value;
  }

  // ---------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------

  async aprovarNFs(): Promise<void> {
    if (this.saving) { return; }
    if (!this.notas.length) {
      this.poNotification.warning('Nenhuma nota selecionada.');
      return;
    }
    if (!this.validar()) { return; }

    this.saving = true;

    // Le os anexos ANTES de aprovar: se algum arquivo estiver ilegivel, o lote
    // nao e aprovado e o operador reanexa com as notas ainda pendentes.
    const anexosPreparados = await this.prepararAnexosDoLote();
    if (!anexosPreparados) {
      this.saving = false;
      return;
    }

    // Gate GED: antes de aprovar, cada nota com anexo precisa ter a pasta GED
    // ja criada no Conexos - senao o upload pos-aprovacao falha silenciosamente
    // (anexo orfao). Decisao: aprova as notas com pasta OK e PULA as com pasta
    // faltando (lista as puladas). Pula tambem notas sem processo (GED-por-
    // processo nao se aplica) e sem anexo.
    const cacheGed = new Map<string, GedFolderCheck>();
    const bloqueadasGed: NotaLote[] = [];
    for (const n of this.notas) {
      const temAnexo = !!(n.danfeFile || n.boletoFile || n.romaneioFile);
      // Sem anexo ou sem processo: GED-por-processo nao se aplica, segue o fluxo.
      if (!temAnexo || !n.processo) { continue; }
      // Pasta GED vazia com anexo + processo: NAO deixar passar. Antes caia no
      // default MV_PNFGDIR no backend, que pode nao existir para o processo.
      if (!n.gedFolder) { bloqueadasGed.push(n); continue; }
      const key = `${n.filial}|${n.processo}|${n.gedFolder}`;
      let check = cacheGed.get(key);
      if (check === undefined) {
        try {
          check = await firstValueFrom(this.anexosService.gedFolderExists({
            filOri: n.filial, processo: n.processo, folder: n.gedFolder
          }));
        } catch {
          // Erro de rede/HTTP na propria chamada: tambem nao e conclusivo.
          check = { exists: false, gedStatus: 'OFFLINE' };
        }
        cacheGed.set(key, check);
      }
      // GED inacessivel (login recusado / fora do ar): NAO pular nota nenhuma.
      // O exists=false aqui nao prova que a pasta falta, e mandar "crie a pasta
      // no Conexos" com o GED fora do ar so faz o operador perder tempo.
      if (check.gedStatus !== 'OK') {
        this.saving = false;
        this.poNotification.error(gedIndisponivelMsg(check.gedStatus)!);
        return;
      }
      if (!check.exists) { bloqueadasGed.push(n); }
    }
    const aprovaveis = this.notas.filter(n => !bloqueadasGed.includes(n));
    if (bloqueadasGed.length > 0) {
      this.poNotification.warning(
        `${bloqueadasGed.length} nota(s) puladas - pasta GED inexistente ou nao informada: ` +
        bloqueadasGed.map(n => {
          const pasta = n.gedFolder ? `["${n.gedFolder}"]` : '[pasta nao informada]';
          return `NF ${n.numeroNf} ${pasta}`;
        }).join('; ')
      );
    }
    if (aprovaveis.length === 0) {
      this.saving = false;
      this.poNotification.warning('Nenhuma nota aprovavel: pasta GED inexistente ou nao informada em todas. Crie ou informe as pastas no Conexos antes de aprovar.');
      return;
    }

    // UMA requisicao POR NOTA, com os anexos junto (regra 2026-08).
    //
    // Antes o lote inteiro ia num unico POST. Como o backend processa ~10s por
    // nota, um lote de 10 passava dos 60s de timeout: o front perdia a resposta,
    // caia no catch e NUNCA chegava a enviar os anexos - enquanto o backend
    // seguia e efetivava tudo. Resultado: nota entregue sem anexo, sem registro
    // do nao-envio, e o operador reenviando o lote duplicava a nota em S2.
    //
    // Agora cada requisicao dura o tempo de UMA nota e o proprio backend grava
    // o anexo no mesmo request (staging antes do MATA103 + vinculo depois).
    let totalOk        = 0;
    let totalErr       = 0;
    let totalAnexosOk  = 0;
    let totalAnexosErr = 0;
    const errosNotas:  string[] = [];
    const errosAnexos: string[] = [];
    const avisosSerie: string[] = [];

    try {
      for (const n of aprovaveis) {
        const preparados = anexosPreparados.get(n) ?? [];
        n.uploadStatus = 'enviando';
        n.uploadMsg    = 'Entregando nota...';

        try {
          const res = await firstValueFrom(this.service.aprovar([this.buildPayload(n, preparados)]));
          const r   = res?.results?.[0];

          if (!r?.ok) {
            totalErr++;
            n.uploadStatus = 'erro';
            n.uploadMsg    = `Falha: ${r?.erro ?? 'erro desconhecido'}`;
            errosNotas.push(`NF ${n.numeroNf}: ${r?.erro ?? 'erro desconhecido'}`);
            continue;
          }

          totalOk++;
          if (r.serie) { n.serie = String(r.serie); }
          if (r.aviso) { avisosSerie.push(`NF ${n.numeroNf}: ${r.aviso}`); }

          const okAnx  = r.anexosOk  ?? 0;
          const errAnx = r.anexosErr ?? 0;
          totalAnexosOk  += okAnx;
          totalAnexosErr += errAnx;

          if (errAnx > 0) {
            n.uploadStatus = 'erro';
            n.uploadMsg    = `Entregue. ${okAnx} anexo(s) OK, ${errAnx} falha(s): ${r.anexosErros ?? ''}`;
            errosAnexos.push(`NF ${n.numeroNf}: ${r.anexosErros ?? 'falha de anexo'}`);
          } else if (okAnx > 0) {
            n.uploadStatus = 'ok';
            n.uploadMsg    = `Entregue. ${okAnx} anexo(s) enviado(s).`;
          } else {
            n.uploadStatus = 'ok';
            n.uploadMsg    = 'Entregue (sem anexos)';
          }
        } catch (err: any) {
          // Falha de comunicacao numa nota NAO interrompe as demais. Como o
          // anexo viaja junto, se o backend efetivou a nota o anexo foi junto -
          // mas daqui nao da para saber, entao a nota fica marcada como sem
          // confirmacao. Reenviar as cegas era exatamente o que duplicava a
          // nota em S2.
          totalErr++;
          const msg = err?.error?.message ?? err?.message ?? 'falha de comunicacao';
          n.uploadStatus = 'erro';
          n.uploadMsg    = `Sem confirmacao (${msg}). Confira em Notas Entregues antes de reenviar.`;
          errosNotas.push(`NF ${n.numeroNf}: sem confirmacao (${msg})`);
        }
      }
    } finally {
      this.saving = false;
    }

    const sufSerie = avisosSerie.length ? ` ${avisosSerie.join(' | ')}` : '';

    if (totalErr === 0 && totalAnexosErr === 0) {
      const sufix = totalAnexosOk > 0 ? ` (${totalAnexosOk} anexo(s) enviado(s))` : '';
      this.poNotification.success(`${totalOk} nota(s) entregue(s) com sucesso${sufix}.${sufSerie}`);
      this.router.navigate(['/notas-list']);
      return;
    }

    if (totalErr === 0) {
      // Notas OK mas anexos com erro - nao navega, deixa o operador ver o status
      this.poNotification.warning(
        `${totalOk} entregue(s), ${totalAnexosOk} anexo(s) OK, ${totalAnexosErr} falha(s) de anexo: ` +
        errosAnexos.join(' | ') + sufSerie
      );
      return;
    }

    const sufAnex = totalAnexosErr > 0
      ? ` Anexos: ${totalAnexosOk} OK, ${totalAnexosErr} falha(s).`
      : '';
    this.poNotification.warning(
      `${totalOk} entregue(s), ${totalErr} com erro: ${errosNotas.join(' | ')}.${sufAnex}${sufSerie}`
    );
  }

  /**
   * Monta o item de aprovacao de UMA nota, ja com os anexos embutidos. Os
   * arquivos foram lidos em prepararAnexosDoLote() antes da aprovacao, entao
   * aqui nao ha releitura de File - e o que fecha a janela de invalidacao.
   */
  private buildPayload(n: NotaLote, preparados: AnexoPreparado[]): AprovarNotaItem {
    // CTe: produto vem do select dedicado (D1_COD da planilha REGRAS CTE),
    // CNAE nao se aplica. NFSe: produto resolvido do CNAE via cnaeList.
    const isCte = n.tipo === 'CTe';
    const produto = isCte
      ? (n.produto || undefined)
      : (this.cnaeList.find(c => c.cnae === n.servicoCnae)?.produto || undefined);

    const anexos: AnexoPayloadItem[] = preparados.map(a => ({
      tipo:         a.tipo,
      filename:     a.filename,
      contentType:  a.contentType,
      base64:       a.base64,
      gedFolder:    n.gedFolder    || undefined,
      cnae:         n.servicoCnae  || undefined,
      dtVencimento: toIsoDateLocal(n.vencimento) || undefined
    }));

    return {
      filial:         n.filial,
      numero:         n.numero,
      serie:          n.serie,
      fornecedor:     n.fornecedorCod,
      loja:           n.fornecedorLoja,
      numeroNf:       n.numeroNf,
      processo:       n.processo       || undefined,
      geraFinanceiro: n.geraFinanceiro || undefined,
      vencimento:     toIsoDateLocal(n.vencimento) || undefined,
      tipoOperacao:   n.tipoOperacao   || undefined,
      cnae:           isCte ? undefined : (n.servicoCnae || undefined),
      produto:        produto,
      // BU do processo (W2_BU via resolveTipoProcesso) - vai pro D1_ITEMCTA
      bu:             n.bu              || undefined,
      // Campos especificos de CTe (backend ignora pra NFSe)
      valorPedagio:    isCte ? (n.valorPedagio || 0)  : undefined,
      simplesNacional: isCte ? n.simplesNacional      : undefined,
      retencao:        isCte ? n.retencao             : undefined,
      // Sem Processo: flag explicita do operador. Vale para CTe de tomador
      // DAAS (backend forca TES 403) e para qualquer especie quando o
      // usuario tem o privilegio SEMPROC - nesse caso o backend exige a TES
      // enviada acima. Chamado 00017826.
      semProcesso:     ((n.podeSemProcesso || (isCte && n.tomadorDaas))
                        && n.semProcesso === 'sim') ? true : undefined,
      anexos:          anexos.length > 0 ? anexos : undefined,
    };
  }

  private validar(): boolean {
    for (let i = 0; i < this.notas.length; i++) {
      const n = this.notas[i];
      const tag = `NF ${n.numeroNf || i + 1}`;
      if (!n.numeroNf) {
        this.poNotification.warning(`${tag}: Numero da NF obrigatorio.`);
        return false;
      }
      // Anexo da NF (Danfe/CTe) obrigatorio ao entregar - regra cliente 2026-07.
      if (!n.danfeFile) {
        this.poNotification.warning(`${tag}: Anexo da NF (Danfe/CTe) eh obrigatorio.`);
        return false;
      }
      if (n.geraFinanceiro === 'sim' && !n.vencimento) {
        this.poNotification.warning(`${tag}: Vencimento obrigatorio quando Gera Financeiro = Sim.`);
        return false;
      }
      // Boleto obrigatorio: regra cliente 2026-05-29 (refinada). So aplica
      // quando geraFinanceiro=Sim AND tipoProcesso='terceiros' (conta e
      // ordem) ou 'encomenda'. Filiais propria / Sem Processo DAAS / sem
      // processo nao exigem boleto.
      const exigeBoleto = n.geraFinanceiro === 'sim'
        && (n.tipoProcessoResolvido === 'terceiros'
            || n.tipoProcessoResolvido === 'encomenda');
      if (exigeBoleto && !n.boletoFile) {
        this.poNotification.warning(
          `${tag}: Anexo Boleto eh obrigatorio quando Gera Financeiro = Sim em processo ` +
          `Conta e Ordem ou Encomenda.`
        );
        return false;
      }
    }
    return true;
  }

  /**
   * Le TODOS os anexos do lote antes da aprovacao. Retorna null quando algum
   * arquivo esta ilegivel - nesse caso o lote NAO deve ser aprovado.
   *
   * Bug que motivou (2026-07): a leitura acontecia depois da aprovacao; um File
   * invalidado pelo SO gerava nota entregue sem anexo na ZOF, sem recuperacao.
   */
  private async prepararAnexosDoLote(): Promise<Map<NotaLote, AnexoPreparado[]> | null> {
    const mapa  = new Map<NotaLote, AnexoPreparado[]>();
    const erros: string[] = [];

    for (const n of this.notas) {
      const tag   = `NF ${n.numeroNf || n.numero}`;
      const itens: AnexoParaPreparar[] = [];

      // O slot "danfe" carrega o documento principal da nota. Para CTe esse
      // documento eh o DACTE: precisa ir com tipo 'CTE' (nao 'DAN') pro backend
      // classificar no GED/Conexos como CTE (MV_PNFCTEC / file_cod 60) e gravar
      // ZOF_TIPO='CTE'. Espelha o fluxo individual (nota-detalhe), onde o
      // operador escolhe 'CTE' no dropdown. Sem isso o lote gravava 'DAN' e o
      // anexo de CTe caia como DANFE.
      if (n.danfeFile) {
        const tipoDoc: AnexoTipo = n.tipo === 'CTe' ? 'CTE' : 'DAN';
        itens.push({ tipo: tipoDoc, file: n.danfeFile, tag });
      }
      if (n.boletoFile)   { itens.push({ tipo: 'BOL', file: n.boletoFile,   tag }); }
      if (n.romaneioFile) { itens.push({ tipo: 'ROM', file: n.romaneioFile, tag }); }

      const r = await this.anexosService.prepararAnexos(itens);
      if (r.erros.length > 0) {
        erros.push(...r.erros);
      }
      mapa.set(n, r.preparados);
    }

    if (erros.length > 0) {
      this.poNotification.error(erros.join(' | '));
      return null;
    }
    return mapa;
  }
}
